/**
 * `entityAnimationPlayback.ts` — the one way past a model's animation SETTINGS
 * to the clip itself: where it is, putting it somewhere, playing it again.
 *
 * Playback is otherwise driven entirely by the persisted settings
 * (`Entities_Executor.toggleModelAnimation`): the renderer diffs them, rebuilds
 * the clip, and the next frame starts it. That is the right model for an
 * EDIT, and it has two holes this closes (AI-1459):
 *   - Writing the settings a model already has does nothing at all, so neither
 *     a replayed workflow step nor "play that again" could restart a take that
 *     had finished.
 *   - The settings cannot say where a clip IS. A revert that put back only the
 *     settings rebuilt the clip and played it from the top — the previous
 *     step's take running again under the current step's narration.
 *
 * A singleton the renderer ATTACHES to rather than the renderer itself under an
 * IOC token, because `Ioc` has no unregister: a viewport that unmounts would
 * leave a disposed renderer resolvable (the trap `ORBIT_CAMERA` documents in
 * `ViewportView.vue`). With nothing attached every call answers "no clip" and
 * changes nothing, which is also the headless answer.
 *
 * Runtime only. Nothing here is persisted or broadcast, so a restart is seen by
 * this viewer alone — the same as a step's video.
 */
import { Ioc } from "@core/ioc/ioc";
import type { EntityAnimationPose } from "@models/data/entityData";

/** What the renderer answers for. Every method is a no-op for a scene or model it has not built. */
export interface EntityAnimationPlaybackHost {
  getEntityAnimationPose(sceneId: string, entityId: string): EntityAnimationPose | null;
  /** Puts the clip at `pose`. False when there is no rendered clip to put there. */
  setEntityAnimationPose(sceneId: string, entityId: string, pose: EntityAnimationPose): boolean;
  /**
   * Plays the model's current take from its start, as its settings say to:
   * an autoplaying model plays it, any other holds its first frame. False when
   * there is no rendered clip.
   */
  restartEntityAnimation(sceneId: string, entityId: string): boolean;
}

export class EntityAnimationPlayback {
  private m_host: EntityAnimationPlaybackHost | null = null;

  attachHost(host: EntityAnimationPlaybackHost): void {
    this.m_host = host;
  }

  /** Only the attached host detaches, so a late dispose cannot drop its successor. */
  detachHost(host: EntityAnimationPlaybackHost): void {
    if (this.m_host === host) this.m_host = null;
  }

  getPose(sceneId: string, entityId: string): EntityAnimationPose | null {
    return this.m_host?.getEntityAnimationPose(sceneId, entityId) ?? null;
  }

  setPose(sceneId: string, entityId: string, pose: EntityAnimationPose): boolean {
    return this.m_host?.setEntityAnimationPose(sceneId, entityId, pose) ?? false;
  }

  restart(sceneId: string, entityId: string): boolean {
    return this.m_host?.restartEntityAnimation(sceneId, entityId) ?? false;
  }
}

export const ENTITY_ANIMATION_PLAYBACK = Symbol.for("EntityAnimationPlayback");

/** The registered service, or `null` before registration (a headless host). */
export function resolveEntityAnimationPlayback(): EntityAnimationPlayback | null {
  return Ioc.isRegistered<EntityAnimationPlayback>(ENTITY_ANIMATION_PLAYBACK)
    ? Ioc.resolve<EntityAnimationPlayback>(ENTITY_ANIMATION_PLAYBACK)
    : null;
}
